/**
 * T13: Detail-Ansicht für 3g-Zulagenblatt-Karten (timesheets mit
 * data.manual_doc_type/meta.doc_type === 'zulagenblatt').
 *
 * Erbt (2026-07-13) die gemeinsame Detail-Basis: DetailViewShell (Kopf + GLEICH
 * HOHE Zwei-Panel + CardDetailActions-Footer) und useCellHighlight. Links die
 * PDF-Vorschau mit column-precisen Overlay-Zellen (T21, ZulagenblattHighlights +
 * spalten_geom), rechts die Ergebnis-Hälfte — nur deren Rendering ist für diesen
 * doc_type ersetzt (Tages-Tabelle 1..31 / Monats-Summen statt von1/bis1-Zellen).
 *
 * Editierbar (card #522): die aufgedruckte Monats-Summenzeile (data.blatt_summe.
 * summen — flaches 9-Key-Objekt, T23 = der Lohnwert) ist in editierbaren States
 * direkt korrigierbar. SELBST-ENTHALTEN: diese View erbt KEINEN Edit-Rahmen (sie
 * steht hinter einem frühen Return in TimesheetDetailPage) — der Summen-Edit,
 * dirty-State + Speichern (PATCH blatt_summe.summen) leben hier. In read-only-
 * States (calculated/exported/archived/superseded) sind die Inputs disabled, kein
 * Speichern (Spiegel des Server-409-Gates).
 *
 * Der Rest bleibt read-only, LAUT mit Unsicherheiten:
 * - zellen_unklar → gelbe Zellen mit Tooltip "unsicher gelesen"
 * - zeilen_uebersprungen → gelber Hinweisblock
 * - fail-loud (grid_mismatch/grid_not_detected) → roter Block statt leerer Tabelle
 */
import { useEffect, useState } from "react";
import { api, type Timesheet } from "../lib/api";
import {
  analyzeDoppelLohnart, doppelRegelTooltip,
  DOPPEL_REGEL_BADGE, DOPPEL_PROBLEM_BADGE,
  type DoppelRegeln,
} from "../lib/doppel-lohnart";
import { ZulagenblattHighlights } from "./ZulagenblattHighlights";
import { DetailViewShell, CardIdChip } from "./DetailViewShell";
import { CardDetailActions } from "./CardDetailActions";
import { useCellHighlight } from "../lib/useCellHighlight";
import { feiertagsHinweis } from "../lib/feiertage-at";
import {
  isZulagenblattData,
  zulagenblattReviewError,
  zulagenColumns,
  zulagenKatalog,
  zulagenSummen,
  zellenUnklarSet,
  formatMenge,
  zulagenblattHighlightModel,
  isZulagenblattReadOnly,
  seedSummenEdits,
  buildSummenPatch,
  type ZulagenblattDay,
} from "../lib/zulagen-render";

export { isZulagenblattData };

/** detail-Objekt des fail-loud-Fehlers kompakt als Key/Value-Zeilen.
 *  grid_mismatch führt erwartet/gefunden — genau die zwei Zahlen, die den
 *  Config-Fix trivial machen. Arrays/Objekte kurz serialisiert. */
function DetailRows({ detail }: { detail: Record<string, unknown> }) {
  const entries = Object.entries(detail);
  if (entries.length === 0) return null;
  return (
    <dl style={{ margin: "8px 0 0", display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px", fontSize: 12 }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: "contents" }}>
          <dt style={{ fontWeight: 600 }}>{k}</dt>
          <dd style={{ margin: 0, overflowWrap: "anywhere" }}>
            {typeof v === "string" || typeof v === "number" || typeof v === "boolean"
              ? String(v)
              : JSON.stringify(v)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

export function ZulagenblattDetail({
  timesheet,
  modal,
  onBack,
  onRefresh,
  highlightReview,
  verdictBusy,
  verdictErr,
  sendHighlightVerdict,
}: {
  timesheet: Timesheet;
  modal?: boolean;
  onBack: () => void;
  /** Karte nach einer CardDetailActions-State-Aktion neu laden (Parent re-fetcht). */
  onRefresh?: () => Promise<void>;
  /** Karte 591: Human-Quality-Gate „Markierung prüfen" — Urteil-Mechanik kommt
   *  vom Parent (TimesheetDetailPage, POST /:id/highlight-review, eine Quelle
   *  für alle timesheet-Typen). Ohne diese Props (alte Aufrufer) kein Gate. */
  highlightReview?: { status: "approved" | "blocked"; by_name?: string; at?: string } | null;
  verdictBusy?: boolean;
  verdictErr?: string | null;
  sendHighlightVerdict?: (status: "approved" | "blocked") => void | Promise<void>;
}) {
  const data = timesheet.data as any;
  const header = (data?.header ?? {}) as {
    employee_raw?: string;
    employee_confidence?: number;
  };
  const meta = (data?.meta ?? {}) as {
    kopf?: { projekt?: string };
    zellen_unklar?: unknown;
    zeilen_uebersprungen?: unknown[];
    zeilen_gelesen?: number;
    spalten_gefunden?: number;
  };
  const days: ZulagenblattDay[] = Array.isArray(data?.days)
    ? (data.days as ZulagenblattDay[]).filter((d) => typeof d?.tag === "number")
    : [];
  const dayByTag = new Map(days.map((d) => [d.tag, d]));
  // Karte 720: dieselbe Abfrage traegt auch die pdf-Namen des Regelwerks — daran
  // haengt die Sichtbarkeit der ABGELEITETEN Felder (Feiertage). Kein zweiter
  // Aufruf; und ohne geladene Matrix erscheint das Feld NICHT (fail-soft nach
  // unten: lieber kein Feld als eines, das den Wert still verschluckt).
  const [matrixPdfNamen, setMatrixPdfNamen] = useState<string[] | null>(null);
  const columns = zulagenColumns(days, data, matrixPdfNamen);
  const summen = zulagenSummen(days, columns);
  // Aufgedruckte Blatt-Summenzeile (ocr blatt_summe) — Selbst-Validierung.
  const blattSumme = ((data as { blatt_summe?: { summen?: Record<string, number> } })
    ?.blatt_summe?.summen) ?? null;
  // T28: berechneter Prozessschritt (calc_model + angewandte Regel + Ergebnis).
  const calc = (data as {
    calculation?: {
      kind?: string;
      rule?: string;
      summen?: Array<{ pdf_art?: string; lohnart?: number | string | null; anzahl?: number; exportierbar?: boolean; kennzeichnung?: string | null; spalten?: string[] }>;
    };
  }).calculation;
  const calcSummen = Array.isArray(calc?.summen) ? calc!.summen : [];

  // T30 (Thomas 14.07.): Doppel-Lohnart-Regeln des Mandanten selbst laden
  // (Zulagenart `pdf` → erwartete Lohnarten + primäre). fail-soft: ohne Matrix
  // entfällt nur der „fehlt-ein-Partner"-Check.
  const [doppelRegeln, setDoppelRegeln] = useState<DoppelRegeln>({});
  useEffect(() => {
    const cid = timesheet.client_id;
    if (!cid) return;
    let cancelled = false;
    api.clientRules(cid)
      .then((res) => {
        if (cancelled || !res) return;
        const zulagen = ((res.ruleset as any)?.uebersetzungsmatrix?.zulagen ?? []) as Array<{
          pdf?: string;
          lohnart?: number | string;
          lohnarten?: Array<number | string>;
        }>;
        const map: DoppelRegeln = {};
        for (const z of zulagen) {
          if (z.pdf && Array.isArray(z.lohnarten) && z.lohnarten.length >= 2) {
            map[z.pdf] = { expected: z.lohnarten, primary: z.lohnart };
          }
        }
        setDoppelRegeln(map);
        setMatrixPdfNamen(zulagen.map((z) => z.pdf).filter((p): p is string => !!p));
      })
      .catch(() => { /* fail-soft */ });
    return () => { cancelled = true; };
  }, [timesheet.client_id]);

  // T30: Doppel-Lohnart-Analyse (geteilter Helper). Anders als beim Excel-Import
  // gibt es hier KEINE Quell-Lohnart je Zeile (der Scan ist nach Zulagenart-
  // Spalten aufgebaut) → primary/derived kommt aus der Matrix (regeln[pdf].primary);
  // Inkonsistenz prüft nur die Anzahl (das Zulagenblatt hat keine Kosten/Basis).
  const doppelInfos = analyzeDoppelLohnart(
    calcSummen.map((s) => ({
      groupKey: String(s.pdf_art ?? ""),
      art: String(s.pdf_art ?? ""),
      lohnart: s.lohnart ?? null,
      anzahl: s.anzahl,
    })),
    doppelRegeln,
  );
  const hasDoppel = doppelInfos.some((i) => i.isDoppel);
  const unklar = zellenUnklarSet(meta.zellen_unklar);
  const uebersprungen = Array.isArray(meta.zeilen_uebersprungen) ? meta.zeilen_uebersprungen : [];
  const reviewError = timesheet.state === "pending_review" ? zulagenblattReviewError(data) : null;
  const projekt = meta.kopf?.projekt ?? data?.projekt ?? "";
  const previewUrl = new URL(
    `api/cards/timesheet/${timesheet.id}/preview-cached`,
    document.baseURI,
  ).toString();

  // Lokaler busy/err-State für die geerbte CardDetailActions-Leiste.
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // card #522: editierbare Blatt-Summenzeile (SELBST-ENTHALTEN — diese View erbt
  // keinen Edit-Rahmen). readOnly spiegelt das Server-Gate (PATCH → 409 ab
  // calculated/exported/archived/superseded): in diesen States kein Edit/Save.
  const readOnly = isZulagenblattReadOnly(timesheet.state);

  // 2026-08-12 (Anlassfall Franka, erster 3G-Testlauf): Wurde auf einem Blatt
  // GAR NICHTS gelesen (Raster nicht erkannt), ist „Geprüft → freigeben" der
  // definierte Weg — die Karte kommt damit zurück in die Bearbeitung, und die
  // Werte trägt der Mensch von Hand ein. Genau dafür fehlte bisher die Fläche:
  // die Eingabetabelle wurde AUS den gelesenen Werten abgeleitet (blattSumme),
  // und ohne gelesene Werte gab es weder Feld noch Speichern-Knopf. Die Karte
  // stand nach der Freigabe editierbar, aber leer da — eine Sackgasse.
  // Deshalb: kein gelesener Wert + editierbar ⇒ leeres Gerüst anbieten.
  // KARTE 724 (Thomas 14.08.2026): Hat ein MENSCH den Typ gewaehlt, bleibt die
  // Karte bei einem Raster-Fehler in „Pruefen" stehen, statt nach „Sonstige"
  // zurueckzufallen (worker/src/handlers/ocr.ts, Vorfahrt des Menschen). Dann
  // MUSS sie hier auch befuellbar sein — sonst steht sie in „Pruefen" und bietet
  // nichts an ausser einer roten Meldung. Genau das war die Sackgasse: die
  // Entscheidung war getroffen, der Weg endete trotzdem.

  const manuellTypisiert =
    (data as { doc_type_source?: unknown } | null | undefined)?.doc_type_source === "manual";
  const summenErfassung = !blattSumme && !readOnly && days.length === 0;
  const summenAnzeige: Record<string, number> | null =
    blattSumme ?? (summenErfassung ? {} : null);

  // Karte 593: MA-Zuordnung DIREKT in der Zulagenblatt-View. Vorher war eine
  // unzugeordnete Karte eine SACKGASSE: kein Select (die View erbt den
  // Stundenlisten-Rahmen nicht), Übernehmen-Knopf komplett versteckt. 31/46
  // März-Karten betroffen (Kopf-Variante „, GZ:" las still leer, Karte 593).
  const [maListe, setMaListe] = useState<Array<{ id: string; first_name: string; last_name: string; dienstnehmernummer: string; ueberstunden_pauschaliert?: boolean | null }>>([]);
  useEffect(() => {
    // 593-Rework (Thomas 24.07.): Wechseln muss auch NACH Zuordnung gehen
    // (wie Diaeten/Stundenliste) — Liste daher in JEDEM editierbaren State.
    if (readOnly) return;
    let cancelled = false;
    api.employees.list(timesheet.client_id)
      .then((emps) => { if (!cancelled) setMaListe(emps as any); })
      .catch(() => { /* fail-soft: ohne Liste bleibt der Hinweis, kein Crash */ });
    return () => { cancelled = true; };
  }, [timesheet.client_id, readOnly]);
  // KARTE 725: Der Halt, bevor geklickt wird. Der Server lehnt ebenfalls ab
  // (hub/src/lib/ueberstunden-pauschale.ts) — hier steht der Grund, damit
  // niemand gegen eine Tür läuft, die schon zu ist. Nur wenn die Karte
  // tatsächlich Üst 50/100 trägt: ohne Überstunden stellt sich die Frage nicht.
  const uestAufKarte = ["ueberstunden_50", "ueberstunden_100"].some((k) => {
    const v = ((data as { blatt_summe?: { summen?: Record<string, unknown> } } | null | undefined)
      ?.blatt_summe?.summen ?? {})[k];
    return typeof v === "number" && Number.isFinite(v) && v > 0;
  });
  // Die Liste wird weiter unten geladen (fail-soft). Solange sie fehlt, wird
  // NICHT gesperrt — der Server hält ohnehin an, und ein Knopf, der ohne Grund
  // klemmt, ist schlimmer als einer, der einmal in eine Absage läuft.
  const zugeordneterMa = maListe.find((m) => m.id === timesheet.employee_id);
  const pauschaleOffen = uestAufKarte
    && !!zugeordneterMa
    && zugeordneterMa.ueberstunden_pauschaliert == null;

  const zuordnen = async (employeeId: string) => {
    if (!employeeId) return;
    setBusy(true);
    setErr(null);
    try {
      await api.timesheets.patch(timesheet.id, { employee_id: employeeId } as any);
      await onRefresh?.();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const blattSummeObj = ((data as { blatt_summe?: { summen?: Record<string, unknown> } })
    ?.blatt_summe?.summen) ?? null;
  const [summenEdits, setSummenEdits] = useState<Record<string, string>>(
    () => seedSummenEdits(blattSummeObj),
  );
  const [dirty, setDirty] = useState(false);
  // Nach Save+Refresh (Parent re-fetcht → neues timesheet-Prop) ODER Kartenwechsel
  // die Edits aus der frisch gelesenen Summe neu seeden und dirty zurücksetzen.
  const blattSummeKey = JSON.stringify(blattSummeObj);
  useEffect(() => {
    setSummenEdits(seedSummenEdits(blattSummeObj));
    setDirty(false);
    // blattSummeKey deckt Kartenwechsel (andere id) UND Wertänderung nach Save ab.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timesheet.id, blattSummeKey]);

  const setSumme = (key: string, value: string) => {
    setSummenEdits((prev) => ({ ...prev, [key]: value }));
    setDirty(true);
  };

  // #506 (alle 4 Typen, Welle 4): der Freigabe-Ausgang aus „Prüfen" — die
  // geerbte Leiste (CardDetailActions) bietet nur „Neu einlesen"; die zweite
  // menschliche Tür (geprüft → freigeben) fehlte dem Zulagenblatt. Erfolg lädt
  // die Karte IN PLACE frisch (onRefresh) — Zustand sofort sichtbar, Button
  // verschwindet, kein Folgeklick-409.
  const approveReview = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api.cards.approveReview("timesheet", timesheet.id, "geprüft, freigegeben");
      await onRefresh?.();
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  const saveSummen = async () => {
    if (readOnly || !dirty || busy) return;
    const { summen, invalid } = buildSummenPatch(summenEdits);
    if (invalid.length > 0) {
      setErr(`Ungültige Zahl in Spalte(n): ${invalid.join(", ")} — bitte korrigieren.`);
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await api.timesheets.patch(timesheet.id, {
        data: {
          ...(data ?? {}),
          blatt_summe: { ...((data as any)?.blatt_summe ?? {}), summen },
        } as any,
      });
      if (onRefresh) await onRefresh();
      setDirty(false);
    } catch (e: any) {
      setErr(e?.message ?? String(e));
    } finally {
      setBusy(false);
    }
  };

  // T21 + Vererbung (2026-07-13): column-precise Highlight Wert ↔ Beleg-Region
  // über den geteilten useCellHighlight-Hook (activeKey = Spalten-Key). Die
  // per-Spalte-x-Geometrie ist GENAU die, die die OCR zum Lesen genutzt hat
  // (data.meta.layout.spalten_geom[<key>]); das y-Band der Summenzeile ist
  // data.blatt_summe.band_pct. Hover eines Summen-/Berechnungs-Werts hebt die
  // EXAKTE Spalten-Zelle in der Summenzeile am Scan hervor — und umgekehrt.
  // NEU: Klick springt zum Gegenstück (Scan-Overlay ↔ Summen-Zeile, scroll+puls).
  const highlight = useCellHighlight<string>();
  const activeKey = highlight.active;
  // BUG-Fix 2026-07-14: die Eignung kommt jetzt aus der reinen, getesteten Funktion
  // zulagenblattHighlightModel (Regression-Guard). hl.reason macht das Fehlen SICHTBAR
  // (statt stillem Bild-Fallback) — insb. „kein_spalten_geom" = Karte vor dem
  // Highlight-Feature (f043f12) gelesen.
  const hl = zulagenblattHighlightModel(data);
  const highlightCells = hl.cells;
  const highlightOn = hl.highlightOn;
  const bandTopPct = hl.bandTopPct;
  const bandBottomPct = hl.bandBottomPct;
  // Amber-Highlight in der rechten Tabelle = gleiche Farbe wie .pdf-overlay-cell.active am Scan.
  const HI_BG = "#fde68a";

  const title = (
    <>
      Zulagenblatt · {timesheet.month}
      <span style={{
        marginLeft: 12, fontSize: 13, fontWeight: 600,
        padding: "2px 8px", borderRadius: 4,
        background: "#ccfbf1", color: "#0f766e",
        border: "1px solid #99f6e4",
      }} title="Mandant 3g — Dokumenttyp Zulagenblatt (Überstunden, Zulagen und Aufwandsentschädigungen)">
        Zulagenblatt
      </span>
    </>
  );

  const source = timesheet.pdf_orig_filename ? (
    <>
      <strong>Quelle:</strong>{" "}
      <a
        href={api.timesheets.pdfUrl(timesheet.id)}
        target="_blank"
        rel="noreferrer"
        title="Original-PDF öffnen"
        style={{ color: "#1d4ed8" }}
      >
        {timesheet.pdf_orig_filename}
      </a>
      {typeof timesheet.pdf_bytes === "number" && (
        <span> · {(timesheet.pdf_bytes / 1024).toFixed(0)} KB</span>
      )}
      <span> · <CardIdChip id={timesheet.id} /></span>
    </>
  ) : null;

  const metaSlot = (
    <>
      <span>
        Mitarbeiter (gelesen): <strong>{header.employee_raw || "—"}</strong>
        {typeof header.employee_confidence === "number" && header.employee_confidence > 0 && (
          <span className="muted" style={{ marginLeft: 4, fontSize: 12 }}>
            ({Math.round(header.employee_confidence * 100)}%)
          </span>
        )}
      </span>
      <span>Projekt: <strong>{projekt || "—"}</strong></span>
      {!readOnly && (
        <label style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          <span style={{ color: timesheet.employee_id ? undefined : "#b45309", fontWeight: 600 }}>
            Mitarbeiter:
          </span>
          {/* 593-Rework: value (kontrolliert) statt defaultValue — der Requeue/
              Refresh bringt neue employee_id, das Select muss folgen. Wechseln
              auch nach Zuordnung erlaubt (Spiegel Diaeten/Stundenliste). */}
          <select disabled={busy} value={timesheet.employee_id ?? ""}
                  onChange={(e) => void zuordnen(e.target.value)}
                  title={timesheet.employee_id
                    ? "Mitarbeiter wechseln — Zuordnung wird sofort gespeichert"
                    : "Der Kopf-Name wurde nicht erkannt/zugeordnet — hier manuell wählen (Voraussetzung fürs Validieren)"}>
            <option value="">— wählen —</option>
            {maListe.map((e) => (
              <option key={e.id} value={e.id}>
                {e.last_name}, {e.first_name} ({e.dienstnehmernummer})
              </option>
            ))}
          </select>
        </label>
      )}
      <span className="ts-state">Status: <strong>{timesheet.state}</strong></span>
    </>
  );

  // Karte 591: „Übernehmen" ohne Urteil → Hinweis statt stiller Block.
  const [gateNotice, setGateNotice] = useState<string | null>(null);
  const gateAktiv = !!sendHighlightVerdict && !readOnly && timesheet.state === "ocr_complete";
  const notices = (
    <>
      {gateAktiv && !highlightReview && (
        <div style={{
          background: "#fffbeb", border: "1px solid #f59e0b", borderLeft: "4px solid #f59e0b",
          padding: "10px 14px", borderRadius: 4, fontSize: 13, color: "#92400e",
          display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10, margin: "8px 0",
        }}>
          <div style={{ flex: "1 1 320px" }}>
            <strong>Markierung prüfen (Human Quality Gate)</strong>
            <div style={{ marginTop: 4 }}>
              Liegen die farbigen Markierungen am Scan auf der richtigen
              Summenzeile und den richtigen Spalten? Dein Urteil wird
              gespeichert, damit wir aus Markierungs-Fehlern lernen — danach
              kannst du validieren.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button type="button" className="primary success" disabled={!!verdictBusy}
                    onClick={() => void sendHighlightVerdict!("approved")}>
              ✓ Markierung stimmt
            </button>
            <button type="button" className="primary" disabled={!!verdictBusy}
                    style={{ background: "#dc2626" }}
                    onClick={() => void sendHighlightVerdict!("blocked")}>
              ✗ Markierung falsch
            </button>
          </div>
        </div>
      )}
      {gateAktiv && highlightReview?.status === "approved" && (
        <div style={{
          fontSize: 12, color: "#166534", background: "#f0fdf4",
          border: "1px solid #bbf7d0", borderRadius: 4, padding: "6px 10px",
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "8px 0",
        }}>
          <span>
            ✓ Markierung freigegeben durch {highlightReview?.by_name ?? "User"}
            {highlightReview?.at ? ` am ${new Date(highlightReview.at).toLocaleString("de-AT")}` : ""}
          </span>
          <button type="button" className="link" disabled={!!verdictBusy}
                  title="Markierung passt doch nicht — sperren (wird gespeichert)"
                  onClick={() => void sendHighlightVerdict!("blocked")}>
            doch sperren
          </button>
        </div>
      )}
      {gateAktiv && highlightReview?.status === "blocked" && (
        <div style={{
          fontSize: 12, color: "#7f1d1d", background: "#fef2f2",
          border: "1px solid #fecaca", borderRadius: 4, padding: "6px 10px",
          display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", margin: "8px 0",
        }}>
          <span>
            ❌ Markierung als falsch gemeldet durch {highlightReview?.by_name ?? "User"}
            {highlightReview?.at ? ` am ${new Date(highlightReview.at).toLocaleString("de-AT")}` : ""}
            {" "}— Urteil ist gespeichert, Validieren bleibt möglich
          </span>
          <button type="button" className="link" disabled={!!verdictBusy}
                  title="Doch in Ordnung — freigeben (wird gespeichert)"
                  onClick={() => void sendHighlightVerdict!("approved")}>
            doch freigeben
          </button>
        </div>
      )}
      {verdictErr && (
        <div className="error-box" style={{ margin: "8px 0" }}>Urteil speichern fehlgeschlagen: {verdictErr}</div>
      )}
      {gateNotice && !highlightReview && (
        <div style={{
          background: "#eff6ff", border: "1px solid #2563eb", borderRadius: 4,
          padding: "8px 12px", fontSize: 13, color: "#1e3a8a", margin: "8px 0",
        }}>{gateNotice}</div>
      )}
      {reviewError && (
        <div style={{
          background: "#fef2f2", border: "1px solid #dc2626", borderLeft: "4px solid #dc2626",
          padding: 14, borderRadius: 4, fontSize: 13, color: "#7f1d1d", margin: "8px 0",
        }}>
          <strong>Lesen fehlgeschlagen — {reviewError.error}</strong>
          <div style={{ marginTop: 6 }}>
            {reviewError.error === "grid_mismatch" ? (
              <>
                Das Spaltenraster passt nicht zur Konfiguration:{" "}
                erwartet <strong>{String(reviewError.detail.erwartet ?? "?")}</strong>,{" "}
                gefunden <strong>{String(reviewError.detail.gefunden ?? "?")}</strong> Spalten.
                Kein Wert wurde übernommen — ein falsches Spalten-Mapping wäre
                stille Lohn-Daten-Korruption.
              </>
            ) : reviewError.error === "grid_not_detected" ? (
              <>
                Auf dem Scan wurde kein Tabellenraster erkannt — Orientierung
                oder Scan-Qualität prüfen. Kein Wert wurde übernommen.
              </>
            ) : (
              <>Der Zulagenblatt-Lesepfad hat einen strukturellen Fehler gemeldet. Kein Wert wurde übernommen.</>
            )}
          </div>
          {manuellTypisiert && (
            <div style={{
              marginTop: 10, padding: "8px 12px", background: "#fff", borderRadius: 4,
              border: "1px solid #fca5a5", color: "#1f2328",
            }}>
              <b>Du hast diese Seite selbst eingeordnet — sie bleibt hier stehen.</b> Trag die
              Monats-Summen unten von Hand ein (der Scan liegt links daneben), dann
              „💾 Summen speichern" und „✓ Geprüft → freigeben". Danach lässt sich die Karte
              wie jede andere übernehmen.
            </div>
          )}
          {/* Die Roh-Geometrie (block_projektion, x_linien_pct …) ist Werkstatt-
              Stoff. Wer die Seite selbst eingeordnet hat, will wissen was zu TUN
              ist — nicht warum der Rasterfit scheiterte. Sichtbar bleibt sie
              trotzdem, einen Klick entfernt (Thomas 14.08.: kundentauglich). */}
          {manuellTypisiert ? (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: "pointer", fontSize: 12, color: "#991b1b" }}>
                Technische Details zum Lesefehler
              </summary>
              <DetailRows detail={reviewError.detail} />
            </details>
          ) : (
            <DetailRows detail={reviewError.detail} />
          )}
        </div>
      )}

      {!reviewError && (data?.ocr_error || uebersprungen.length > 0) && (
        <div className="warning-box">
          {data?.ocr_error && (
            <div>OCR-Fehler: <code>{String(data.ocr_error)}</code></div>
          )}
          {uebersprungen.length > 0 && (
            <div>
              {uebersprungen.length} Zeile{uebersprungen.length === 1 ? "" : "n"} beim Lesen übersprungen:{" "}
              <code style={{ fontSize: 11 }}>{JSON.stringify(uebersprungen)}</code>
            </div>
          )}
        </div>
      )}
    </>
  );

  // LINKS: PDF-Vorschau mit column-precisen Overlay-Zellen (T21) — Hover/Klick
  // hebt die EXAKTE Spalten-Zelle in der aufgedruckten Summenzeile hervor. Ohne
  // Geometrie (alte/fehlgeschlagene Karte) → plain Bild-Fallback.
  const left = highlightOn ? (
    <ZulagenblattHighlights
      imageUrl={previewUrl}
      pdfHrefForOpen={api.timesheets.pdfUrl(timesheet.id)}
      cells={highlightCells}
      bandTopPct={bandTopPct}
      bandBottomPct={bandBottomPct}
      activeKey={activeKey}
      onHover={highlight.setActive}
      onCellClick={(key) => highlight.jumpTo("value", [key])}
    />
  ) : (
    <div style={{ overflow: "auto", height: "100%" }}>
      <a
        href={api.timesheets.pdfUrl(timesheet.id)}
        target="_blank"
        rel="noreferrer"
        title="Original-PDF öffnen"
      >
        <img
          src={previewUrl}
          alt="Zulagenblatt-Scan"
          style={{
            display: "block", maxWidth: "100%", maxHeight: "85vh",
            border: "1px solid #e5e7eb", borderRadius: 4, background: "#fff",
          }}
          onError={(e) => { (e.target as HTMLImageElement).style.display = "none"; }}
        />
      </a>
    </div>
  );

  // RECHTS: die gelesenen Werte + Berechnung.
  const right = (
    <div style={{ padding: 12 }}>
      {/* T23: nur die aufgedruckte Monats-Summenzeile wird erfasst (Tage
          1–31 werden nicht verwendet). days ist leer → Summen-Ansicht;
          zum Verifizieren die Quelle (PDF) links daneben. */}
      {(!reviewError || manuellTypisiert) && days.length === 0 && summenAnzeige && (
        <>
          <div className="ts-data-hint">
            {summenErfassung ? (
              <>
                Auf diesem Blatt wurde <b>nichts gelesen</b>. Trage die
                {" "}<b>Monats-Summen</b> je Zulagenart hier von Hand ein — der Scan links
                zeigt das Original. Nur die Summe ist der Lohnwert; die Tage 1–31
                werden nicht erfasst.{" "}
              </>
            ) : (
              <>
                Gelesene <b>Monats-Summen</b> je Zulagenart (aus der aufgedruckten
                Summenzeile). Die Tage 1–31 werden nicht erfasst — nur die Summe
                ist der Lohnwert.{" "}
              </>
            )}
            {highlightOn ? (
              <>
                <span style={{
                  background: "rgba(245,158,11,0.30)", border: "1px solid #f59e0b",
                  borderRadius: 3, padding: "0 5px",
                }}>markiert</span>{" "}
                Über einen Wert fahren hebt <b>genau diese Spalte</b> in der
                aufgedruckten Summenzeile am Scan hervor; <b>Klick</b> scrollt sie
                dort hin und pulst sie (und umgekehrt) — dieselbe Spalten-Zelle,
                die die OCR gelesen hat.
              </>
            ) : (
              <>
                Links das Original zum Abgleich gegen die unterste Zeile am Blatt.
                {hl.reason === "kein_spalten_geom" && (
                  <span style={{ color: "#b45309" }}>
                    {" "}· <b>Kein spaltengenaues Highlight</b> für diese Karte: sie wurde vor dem
                    Highlight-Feature eingelesen (keine Spalten-Geometrie gespeichert).
                    „↻ Neu einlesen (OCR)" unten erzeugt sie.
                  </span>
                )}
              </>
            )}
          </div>
          {/* Karte 720: Der Kalender-Hinweis erscheint NUR, wenn das Feld
              „Feiertage (EFZG)" wirklich da ist — also wenn das Regelwerk seinen
              pdf-Namen kennt. Er zeigt ALLE gesetzlichen Feiertage des Monats mit
              Wochentag, ohne Mo-Fr-Filter: Bei 3G hat kein Dienstnehmer einen
              Sollzeitplan (0 von 94), eine Werktags-Annahme waere also unbelegt.
              Franka entscheidet, wir liefern die vollstaendige Information. */}
          {zulagenKatalog(data, matrixPdfNamen).some((c) => c.key === "feiertag")
            && feiertagsHinweis(timesheet.month).length > 0 && (
            <div style={{
              margin: "0 0 10px", padding: "8px 12px", borderRadius: 6,
              background: "#fff8e5", border: "1px solid #f0dca0", fontSize: 12.5,
            }}>
              <strong>Gesetzliche Feiertage in diesem Monat:</strong>{" "}
              {feiertagsHinweis(timesheet.month)
                .map((f) => `${f.name} (${f.wochentag}, ${f.tag}.${Number(timesheet.month.slice(5, 7))}.)`)
                .join(" · ")}
              <div className="muted" style={{ marginTop: 4 }}>
                Trag unten bei „Feiertage (EFZG)" ein, wie viele davon für diesen
                Dienstnehmer zählen — also an denen er nicht gearbeitet hat.
              </div>
            </div>
          )}
          <div style={{ overflowX: "auto" }}>
            <table className="ts-table" style={{ fontSize: 13 }}>
              <thead>
                <tr>
                  <th>Zulagenart</th>
                  <th style={{ textAlign: "right" }}>
                    {readOnly ? "Aufgedruckte Summe" : "Summe (editierbar)"}
                  </th>
                </tr>
              </thead>
              <tbody>
                {/* Thomas 13.08.: Der Feldkatalog ist UNSERE Definition — die
                    Lesung entscheidet nicht, welche Felder es gibt. Vorher stand
                    hier `summenErfassung || typeof summenAnzeige[c.key] === "number"`:
                    ein nicht gelesenes Feld verschwand von der Karte und war damit
                    weder anzweifelbar noch korrigierbar (gemessen an Kada
                    `556d1f3c`: 4 von 10 Zeilen, der bewilligte Urlaubstag fehlte,
                    obwohl im Scan eine 6 steht).
                    Das ist DIESELBE Lehre wie beim Leerfall am 12.08. — dort wurde
                    das leere Gerüst schon nachgerüstet ("kein gelesener Wert +
                    editierbar ⇒ leeres Gerüst anbieten"), nur der TEILFALL blieb
                    offen. Jetzt gilt sie vollständig. */}
                {zulagenKatalog(data, matrixPdfNamen).map((c) => {
                  const lit = highlightOn && activeKey === c.key;
                  return (
                    <tr
                      key={c.key}
                      {...(highlightOn ? highlight.linkProps({ side: "value", key: c.key }) : {})}
                      style={{ cursor: highlightOn ? "pointer" : "default", ...(lit ? { background: HI_BG } : {}) }}
                    >
                      <td>{c.label}</td>
                      {/* #479-Rework (Thomas 23.07.): vorher fontWeight 700 nur bei Summe≠0 —
                          wirkte als zufällige Fett-Inkonsistenz (Untertage/Baustelle/Trennung
                          fett, Rest nicht). Die 0-Unterscheidung trägt jetzt allein die Farbe
                          (grau = aufgedruckte Summe 0) + erklärender Tooltip; Schriftgewicht
                          ist einheitlich. */}
                      <td title={summenAnzeige[c.key] ? undefined : "Aufgedruckte Summe ist 0"}
                          style={{ textAlign: "right", fontWeight: 400,
                                   color: summenAnzeige[c.key] ? "#0f172a" : "#94a3b8" }}>
                        {readOnly ? (
                          summenAnzeige[c.key]
                        ) : (
                          <input
                            type="text"
                            inputMode="decimal"
                            aria-label={`Summe ${c.label}`}
                            value={summenEdits[c.key] ?? ""}
                            disabled={busy}
                            onChange={(e) => setSumme(c.key, e.target.value)}
                            // Klick/Fokus im Feld darf NICHT den Zeilen-„Sprung zum
                            // Scan-Overlay" (highlight.linkProps onClick) auslösen.
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            style={{
                              width: 90, textAlign: "right", fontSize: 13,
                              padding: "2px 6px", border: "1px solid #cbd5e1", borderRadius: 4,
                            }}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* T28: „Details was berechnet wurde" — das Ergebnis des calc-Schritts
              (Regelwerk zulagen_summen_v0): je Zulagenart das RZL-Lohnart-Mapping
              + Anzahl. Doppel-Lohnart (Baustelle → 13 UND 14) als zwei Zeilen.
              Gelb = keine Lohnart (nicht exportierbar). */}
          {calc && (
            <div className="calc-panel" style={{ marginTop: 16 }}>
              <h3>Berechnung · was in den RZL-Export geht</h3>
              <dl style={{
                margin: "0 0 10px", display: "grid", gridTemplateColumns: "auto 1fr",
                gap: "3px 12px", fontSize: 12, background: "#f8fafc",
                border: "1px solid #e2e8f0", borderRadius: 4, padding: "8px 10px",
              }}>
                <dt style={{ fontWeight: 600, color: "#475569" }}>Berechnungsmodell</dt>
                <dd style={{ margin: 0 }}><code>{calc.kind ?? "—"}</code></dd>
                <dt style={{ fontWeight: 600, color: "#475569" }}>Regel</dt>
                <dd style={{ margin: 0 }}>{calc.rule ?? "—"}</dd>
              </dl>
              <div style={{ marginBottom: 10, fontSize: 12 }}>
                <span style={{
                  background: "#fef9c3", border: "1px solid #eab308", borderRadius: 3,
                  padding: "0 5px", marginRight: 6,
                }}>gelb</span>
                = keine RZL-Lohnart (nicht exportierbar — fehlt im Export).
              </div>
              {hasDoppel && (
                <div style={{ marginBottom: 10, fontSize: 12, lineHeight: 1.5 }}>
                  <span style={{ color: "#475569", marginRight: 4 }}>↳</span>
                  eingerückte Zeile mit <span style={{ ...DOPPEL_REGEL_BADGE, marginLeft: 0 }}>Regel ⓘ</span>
                  {" "}= regel-erzeugte Lohnart (z. B. Baustellenzulage → 13 + 14; gleiche Basis,
                  SV-Splittung macht RZL — keine Doppelzahlung).
                  {"  "}<span style={{ ...DOPPEL_PROBLEM_BADGE, marginLeft: 0 }}>⚠</span>
                  {" "}= kaputtes Paar (unvollständig / inkonsistent) — das Warum steht im Hover.
                </div>
              )}
              {calcSummen.length === 0 ? (
                <div className="muted" style={{ fontSize: 12 }}>Keine berechneten Zeilen.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table className="ts-table" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th>Zulagenart</th>
                        <th>Lohnart</th>
                        <th style={{ textAlign: "right" }}>Anzahl</th>
                      </tr>
                    </thead>
                    <tbody>
                      {calcSummen.map((s, i) => {
                        const info = doppelInfos[i]!;
                        const offen = s.lohnart === null || s.lohnart === undefined || s.exportierbar === false;
                        // Berechnungs-Zeile ↔ Spalten-Zelle: calc.summen[].spalten
                        // trägt die Zulagenart-Spalten-Keys (z. B. ["untertage"]).
                        const colKeys = Array.isArray(s.spalten) ? s.spalten : [];
                        const cellable = highlightOn && colKeys.some((k) => highlightCells.some((hc) => hc.key === k));
                        const lit = highlightOn && activeKey !== null && colKeys.includes(activeKey);
                        const lohnartText = s.lohnart === null || s.lohnart === undefined ? "— (nicht exportierbar)" : String(s.lohnart);
                        return (
                          <tr key={`${s.pdf_art}-${String(s.lohnart)}-${i}`}
                              {...(cellable ? highlight.linkProps({ side: "value", key: colKeys[0]!, match: colKeys }) : {})}
                              style={{
                                cursor: cellable ? "pointer" : "default",
                                ...(offen ? { background: "#fef9c3" } : {}),
                                ...(info.problem ? { background: "#fef2f2" } : {}),
                                ...(lit ? { background: offen ? "#fbbf24" : HI_BG } : {}),
                              }}
                              title={offen
                                ? "Keine RZL-Lohnart — fehlt im Export" + (cellable ? " · Klick: Spalte am Scan hervorheben" : "")
                                : (cellable ? "Klick: genau diese Spalte in der Summenzeile am Scan hervorheben" : undefined)}>
                            <td>{s.pdf_art ?? "—"}{s.kennzeichnung ? ` (${s.kennzeichnung})` : ""}</td>
                            <td>
                              {info.isAbgeleitet
                                ? <span style={{ paddingLeft: 12, color: "#475569" }}>↳ {lohnartText}</span>
                                : lohnartText}
                              {info.isDoppel && info.isAbgeleitet && (
                                <span style={DOPPEL_REGEL_BADGE} title={doppelRegelTooltip(String(s.pdf_art ?? ""), info.partner)}>Regel ⓘ</span>
                              )}
                              {info.problem && (
                                <span style={DOPPEL_PROBLEM_BADGE} title={info.problem.warum}>⚠ {info.problem.kurz}</span>
                              )}
                            </td>
                            <td style={{ textAlign: "right", fontWeight: 600 }}>{formatMenge(s.anzahl)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
      {/* T23 + Thomas 14.08.2026: „wir haben gesagt wir lesen nur die Summen-Zeile,
          wie kannst du also 1–31 lesen?" — Richtig, wir lesen sie nicht. Diese
          Tages-Tabelle stammt aus der Zeit VOR T23, als der Leser noch Tageszeilen
          erzeugte. Die alte Bedingung `!(days.length === 0 && blattSumme)` liess
          sie durch, sobald eine Karte KEINE aufgedruckte Summenzeile hatte: dann
          stand dort ein leeres 31-Zeilen-Raster unter der Ueberschrift „Gelesene
          Werte des Zulagenblatts — Gelesen: 0 Zeilen". Eine Anzeige, die
          „gelesen" behauptet, wo nichts gelesen wurde, ist schlimmer als keine.
          GEMESSEN 14.08. auf prod: 0 von 51 Zulagenblatt-Karten tragen ueberhaupt
          Tageszeilen. Die Tabelle erscheint jetzt nur noch, wenn wirklich welche
          da sind — bei Alt-Karten aus der Zeit davor. */}
      {!reviewError && days.length > 0 && (
        <>
          <div className="ts-data-hint">
            Gelesene Werte des Zulagenblatts — leere Zellen (0 / kein Häkchen)
            sind dezent, gefüllte deutlich.{" "}
            {unklar.size > 0 && (
              <>
                <span style={{
                  background: "#fef9c3", border: "1px solid #eab308",
                  borderRadius: 3, padding: "0 4px",
                }}>gelb</span>
                {" "}= unsicher gelesen (Wert prüfen, steht auf 0).{" "}
              </>
            )}
            Gelesen: {meta.zeilen_gelesen ?? days.length} Zeilen
            {typeof meta.spalten_gefunden === "number" ? ` · ${meta.spalten_gefunden} Spalten` : ""}.
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="ts-table" style={{ fontSize: 12 }}>
              <thead>
                <tr>
                  <th>Tag</th>
                  {columns.map((c) => (<th key={c.key} title={c.key}>{c.label}</th>))}
                  <th title="Bewilligter Urlaubstag">Urlaub</th>
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 31 }, (_, i) => i + 1).map((tag) => {
                  const day = dayByTag.get(tag);
                  return (
                    <tr key={tag}>
                      <td className="day-col">{tag}</td>
                      {columns.map((c) => {
                        const v = day?.zulagen?.[c.key];
                        const text = formatMenge(v);
                        const isUnklar = unklar.has(`${tag}:${c.key}`);
                        return (
                          <td
                            key={c.key}
                            title={isUnklar ? "unsicher gelesen" : undefined}
                            style={{
                              textAlign: "right", padding: "2px 6px",
                              ...(isUnklar
                                ? { background: "#fef9c3", outline: "1px solid #eab308", outlineOffset: -1 }
                                : {}),
                              ...(text
                                ? { fontWeight: 600 }
                                : { color: "#c4c8ce" }),
                            }}
                          >
                            {text || (day ? "·" : "")}
                          </td>
                        );
                      })}
                      <td
                        title={unklar.has(`${tag}:urlaubstag`) ? "unsicher gelesen" : undefined}
                        style={{
                          textAlign: "center",
                          ...(unklar.has(`${tag}:urlaubstag`)
                            ? { background: "#fef9c3", outline: "1px solid #eab308", outlineOffset: -1 }
                            : {}),
                          ...(day?.urlaubstag ? { fontWeight: 600 } : { color: "#c4c8ce" }),
                        }}
                      >
                        {day?.urlaubstag ? "✓" : day ? "·" : ""}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ borderTop: "2px solid #94a3b8", background: "#f8fafc" }}>
                  <td className="day-col" style={{ fontWeight: 700 }}>Σ&nbsp;gelesen</td>
                  {columns.map((c) => {
                    const ger = Number(summen.spalten[c.key] ?? 0);
                    const auf = blattSumme?.[c.key];
                    const mismatch = typeof auf === "number" && auf !== ger;
                    return (
                      <td key={c.key} style={{
                        textAlign: "right", padding: "2px 6px", fontWeight: 700,
                        ...(mismatch ? { background: "#fee2e2", color: "#b91c1c" } : {}),
                      }}>
                        {formatMenge(summen.spalten[c.key]) || "0"}
                      </td>
                    );
                  })}
                  <td style={{ textAlign: "center", fontWeight: 700 }}>{summen.urlaubstage}</td>
                </tr>
                {blattSumme && (
                  <tr style={{ background: "#f8fafc", color: "#475569" }}>
                    <td className="day-col" style={{ fontWeight: 600 }} title="am Blatt aufgedruckte Monatssumme">aufgedruckt</td>
                    {columns.map((c) => {
                      const auf = blattSumme[c.key];
                      const ger = Number(summen.spalten[c.key] ?? 0);
                      const mismatch = typeof auf === "number" && auf !== ger;
                      return (
                        <td key={c.key} style={{
                          textAlign: "right", padding: "2px 6px",
                          ...(mismatch ? { background: "#fee2e2", color: "#b91c1c", fontWeight: 700 } : {}),
                        }}>
                          {typeof auf === "number" ? auf : "–"}
                        </td>
                      );
                    })}
                    <td />
                  </tr>
                )}
              </tfoot>
            </table>
          </div>
          <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
            Σ gelesen = aus den gelesenen Tagen berechnet; <b>aufgedruckt</b> = die
            Monatssumme vom Blatt. <span style={{ color: "#b91c1c" }}>Rot markierte Spalten</span> weichen
            ab — dort hat die OCR Zellen verpasst (Wert am Scan links prüfen und Tag ergänzen).
          </div>
        </>
      )}
      {reviewError && (
        <div className="muted" style={{ fontSize: 13, padding: 12 }}>
          Keine gelesenen Werte — Details im roten Fehlerblock oben.
          Der Scan links zeigt das Original zur Prüfung.
        </div>
      )}
    </div>
  );

  // Geerbte Standard-Aktionen (Reopen/Export/Revert „↻ Zurück zur Bearbeitung")
  // aus der kind-agnostischen CardDetailActions-Leiste — nur wenn der Parent
  // onRefresh liefert.
  const actions = onRefresh ? (
    <CardDetailActions
      kind="timesheet"
      cardId={timesheet.id}
      month={timesheet.month}
      clientId={timesheet.client_id}
      state={timesheet.state}
      busy={busy}
      err={err}
      setBusy={setBusy}
      setErr={setErr}
      onRefresh={onRefresh}
      allowInlineValidate={true}
      inlineValidateDisabledReason={!timesheet.employee_id
        ? "Zuerst Mitarbeiter zuordnen (oben „Mitarbeiter“) — der Kopf-Name wurde nicht erkannt/zugeordnet."
        : pauschaleOffen
          ? "Überstundenpauschale nicht hinterlegt: Diese Karte trägt Überstunden (50 %/100 %). "
            + "Ohne die Angabe beim Dienstnehmer kann nicht entschieden werden, ob sie in den Export "
            + "gehören — bitte unter Einstellungen → Mitarbeiter hinterlegen."
          : undefined}
      beforeInlineValidate={gateAktiv ? () => {
        if (!highlightReview) {
          setGateNotice(
            "Magst du vor dem Validieren kurz die Markierung prüfen? Ein Klick auf "
            + "„Markierung stimmt“ oder „Markierung falsch“ (oben im gelben Feld) genügt "
            + "— dein Urteil hilft uns, die automatische Erkennung laufend zu verbessern.");
          return false;
        }
        return true;
      } : undefined}
    >
      {/* #506 (alle 4 Typen): Freigabe-Ausgang aus „Prüfen" — Ergänzung zur
          geerbten „↻ Neu einlesen (OCR)"-Aktion (CardDetailActions). */}
      {timesheet.state === "pending_review" && (
        <button
          type="button"
          className="primary success"
          onClick={() => void approveReview()}
          disabled={busy}
          title="Geprüft und in Ordnung → weiter zu OCR fertig (danach übernehmbar). Die Werte bleiben unverändert."
        >
          ✓ Geprüft → freigeben
        </button>
      )}
      {/* card #522: Summen-Speichern — nur wenn eine Summenzeile existiert und
          die Karte editierbar ist. disabled solange nichts geändert (dirty) oder
          read-only/busy. */}
      {(blattSumme || summenErfassung) && !readOnly && (
        <button
          type="button"
          className="primary"
          onClick={() => void saveSummen()}
          disabled={busy || !dirty}
          title={dirty
            ? "Geänderte Monats-Summen speichern (blatt_summe.summen)"
            : "Keine Änderung — nichts zu speichern"}
        >
          💾 Summen speichern{dirty ? " *" : ""}
        </button>
      )}
    </CardDetailActions>
  ) : null;

  return (
    <DetailViewShell
      modal={modal}
      onBack={onBack}
      title={title}
      source={source}
      meta={metaSlot}
      notices={notices}
      left={left}
      right={right}
      highlight={highlight}
      actions={actions}
    />
  );
}
